import { useEffect } from 'react';

import { FcCancel, FcCheckmark } from 'react-icons/fc';
import { RiSpam2Fill } from 'react-icons/ri';
import styled from 'styled-components';

import { useFieldArray, useFormContext } from 'react-hook-form';

import { GetUserQuery, UserState } from 'data/__generated__';

import { DetailFormSelect, DetailFormValue, DetailValue } from 'components/DetailsView';
import { TotemLabel } from 'components/TotemLabel';

import { UserDetailsFormData } from 'pages/Users/UserDetails/UserDetails';
import { INITIAL_USER_STATES, USER_STATES_LABELS } from '../constants/states';
import { UserEmail } from './types';

type Props = {
    user: NonNullable<GetUserQuery['userFromAdmin']>;
};

export const IdentityContactSection = ({ user }: Props) => {
    const { register, setValue } = useFormContext<UserDetailsFormData>();

    const {
        emails,
        profile: { firstname, lastname, state, phone },
        username,
    } = user;

    const { fields: emailFields } = useFieldArray<
        {
            emails: UserEmail[];
        },
        'emails',
        'id'
    >({ name: 'emails' as const });

    useEffect(() => {
        if (emails) {
            setValue(
                'emails',
                emails.map(({ address, verified, bounced }) => ({ address, verified, bounced })),
            );
        }
    }, [emails, setValue]);

    const statesOptions = (Object.keys(USER_STATES_LABELS) as UserState[]).map((key) => ({
        label: USER_STATES_LABELS[key],
        value: key,
        isDisabled: INITIAL_USER_STATES.includes(key),
    }));

    return (
        <Container>
            <DetailFormValue label="Username" defaultValue={username} {...register('username')} />
            <div>
                <TotemLabel>Emails</TotemLabel>
                <EmailsListContainer>
                    {emailFields?.map((email, index) => {
                        const { address, bounced, verified } = email;
                        return (
                            <EmailFieldContainer key={email.id}>
                                <DetailFormValue
                                    placeholder="Adresse email"
                                    defaultValue={address}
                                    {...register(`emails.${index}.address`)}
                                />
                                {bounced ? (
                                    <RiSpam2Fill size={19} />
                                ) : verified ? (
                                    <FcCheckmark size={19} />
                                ) : (
                                    <FcCancel size={19} />
                                )}
                            </EmailFieldContainer>
                        );
                    })}
                </EmailsListContainer>
            </div>
            <Grid>
                <DetailValue label="Prénom" value={firstname} />
                <DetailValue label="Nom" value={lastname} />
                <DetailValue label="Téléphone" value={phone} />
                <DetailFormSelect<string | null>
                    label="Statut"
                    placeholder="Statut"
                    defaultValue={state}
                    name="state"
                    options={statesOptions}
                    required
                />
            </Grid>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
`;

const EmailsListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const EmailFieldContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    & > :first-child {
        flex: 1;
        min-width: 0;
    }
`;
